import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWallet as useWalletHook, useWalletTransactions, useCommunityFunds, useHealthcareProviders, QUERY_KEYS } from "@/hooks/use-queries";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useRoute, Link, useLocation } from "wouter";
import { useAnimatedCounter } from "@/hooks/use-animated-counter";
import { useConfetti } from "@/components/confetti-burst";
import { ThankYouPrompt, SentThankYous } from "@/components/thank-you-flow";
import {
  Wallet,
  Heart,
  Users,
  TrendingUp,
  Share2,
  Copy,
  Plus,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeft,
  ExternalLink,
  CheckCircle,
  Clock,
  DollarSign,
  Building2,
  UserPlus,
  ShieldCheck,
  CreditCard,
  Send,
  XCircle,
  AlertCircle,
  Hospital,
  UserCheck,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WalletCollaboration } from "@/components/wallet-collaboration";

interface WalletData {
  id: string;
  userId: string;
  patientName: string;
  category: string;
  description?: string;
  balance: number;
  totalContributions: number;
  totalSpent: number;
  goalAmount?: number;
  isPublic: boolean;
  shareToken: string;
  createdAt: string;
  walletType?: string;
  organizationName?: string;
  organizationType?: string;
  taxId?: string;
  verificationStatus?: string;
  primaryAdministratorId?: string;
  beneficiarySpendingLimit?: number;
  requiresApprovalForPayments?: boolean;
  paymentApprovalThreshold?: number;
  joinToken?: string | null;
  joinTokenEnabled?: boolean;
}

interface WalletBeneficiary {
  id: string;
  walletId: string;
  firstName: string;
  lastName: string;
  relationship?: string;
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  isActive: boolean;
  spendingLimit?: number;
  totalSpent: number;
  verificationStatus: string;
  verifiedAt?: string;
  eligibilityNotes?: string;
  addedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface WalletAdministrator {
  id: string;
  walletId: string;
  userId: string;
  role: string;
  permissions: string[];
  invitedBy?: string;
  invitedAt: string;
  acceptedAt?: string;
  status: string;
  createdAt: string;
}

interface ProviderPayment {
  id: string;
  walletId: string;
  providerId: string;
  beneficiaryId?: string;
  walletTransactionId?: string;
  amount: number;
  serviceDate?: string;
  serviceDescription?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  status: string;
  requestedBy: string;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  processedAt?: string;
  paymentReference?: string;
  paymentMethod?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface HealthcareProvider {
  id: string;
  name: string;
  providerType: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  verificationStatus: string;
  isActive: boolean;
}

interface Transaction {
  id: number;
  walletId: string;
  type: string;
  amount: number;
  description?: string;
  contributorName?: string;
  orderId?: string;
  status: string;
  createdAt: string;
}

interface CommunityFund {
  id: string;
  name: string;
  category: string;
  description?: string;
  balance: number;
  totalRaised: number;
  totalDistributed: number;
  isActive: boolean;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function WalletPage() {
  usePageTitle("Health Wallet", "View and manage your personal health wallet, contributions, and transaction history.");
  const { toast } = useToast();
  const { user } = useAuth();
  const [, params] = useRoute("/wallet/:walletId");
  const walletIdFromUrl = params?.walletId;
  const [isAddBeneficiaryOpen, setIsAddBeneficiaryOpen] = useState(false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);

  const { data: walletById, isLoading: walletByIdLoading } = useQuery<WalletData | null>({
    queryKey: ["/api/wallets", walletIdFromUrl],
    enabled: !!walletIdFromUrl,
  });

  const { data: myWallet, isLoading: myWalletLoading } = useWalletHook({
    enabled: !walletIdFromUrl,
  });

  const wallet = walletIdFromUrl ? walletById : myWallet;
  const walletLoading = walletIdFromUrl ? walletByIdLoading : myWalletLoading;
  const isOwner = !!wallet && !!user && wallet.userId === user.id;

  const { data: transactionsByUrl, isLoading: transactionsByUrlLoading } = useQuery<Transaction[]>({
    queryKey: ["/api/wallets", walletIdFromUrl, "transactions"],
    enabled: !!walletIdFromUrl && !!walletById,
  });

  const { data: myTransactions, isLoading: myTransactionsLoading } = useWalletTransactions({
    enabled: !walletIdFromUrl && !!myWallet,
  });

  const transactions = walletIdFromUrl ? transactionsByUrl : myTransactions;
  const transactionsLoading = walletIdFromUrl ? transactionsByUrlLoading : myTransactionsLoading;

  const { data: communityFunds } = useCommunityFunds();

  const { data: beneficiaries, isLoading: beneficiariesLoading } = useQuery<WalletBeneficiary[]>({
    queryKey: ["/api/wallets", wallet?.id, "beneficiaries"],
    enabled: !!wallet && (wallet.walletType === "family" || wallet.walletType === "community" || wallet.walletType === "organization"),
  });

  const { data: administrators, isLoading: administratorsLoading } = useQuery<WalletAdministrator[]>({
    queryKey: ["/api/wallets", wallet?.id, "administrators"],
    enabled: !!wallet && wallet.walletType !== "individual",
  });

  const { data: providerPayments, isLoading: paymentsLoading } = useQuery<ProviderPayment[]>({
    queryKey: ["/api/wallets", wallet?.id, "provider-payments"],
    enabled: !!wallet,
  });

  const { data: healthcareProviders } = useHealthcareProviders({
    enabled: !!wallet,
  });

  const addBeneficiaryMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; relationship?: string; email?: string; phone?: string; spendingLimit?: number }) => {
      const response = await apiRequest("POST", `/api/wallets/${wallet?.id}/beneficiaries`, {
        ...data,
        spendingLimit: data.spendingLimit ? data.spendingLimit * 100 : undefined,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/wallets", wallet?.id, "beneficiaries"] });
      setIsAddBeneficiaryOpen(false);
      toast({
        title: "Beneficiary Added",
        description: "The beneficiary has been added to your wallet.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add beneficiary",
        variant: "destructive",
      });
    },
  });

  const updateWalletSettingsMutation = useMutation({
    mutationFn: async (data: { isPublic?: boolean; category?: string }) => {
      const response = await apiRequest("PATCH", "/api/wallet", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wallet });
      toast({
        title: "Settings Updated",
        description: "Your wallet settings have been saved.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update wallet settings",
        variant: "destructive",
      });
    },
  });

  const handleTogglePublic = async () => {
    if (!wallet) return;
    const newValue = !wallet.isPublic;
    setIsTogglingPublic(true);
    try {
      const response = await fetch(`/api/wallets/${wallet.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic: newValue }),
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update visibility");
      }
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wallet });
      await queryClient.invalidateQueries({ queryKey: ["/api/wallets", wallet.id] });
      toast({ title: "Visibility updated" });
    } catch (error: any) {
      // Revert by re-syncing from the server
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wallet });
      await queryClient.invalidateQueries({ queryKey: ["/api/wallets", wallet.id] });
      toast({
        title: "Error",
        description: error.message || "Failed to update visibility",
        variant: "destructive",
      });
    } finally {
      setIsTogglingPublic(false);
    }
  };

  const handleCategoryChange = (newCategory: string) => {
    if (wallet) {
      updateWalletSettingsMutation.mutate({ category: newCategory });
    }
  };

  const copyShareLink = () => {
    if (wallet?.shareToken) {
      const link = `${window.location.origin}/wallet/contribute/${wallet.shareToken}`;
      navigator.clipboard.writeText(link);
      toast({
        title: "Link Copied",
        description: "Share this link with your support network to receive contributions.",
      });
    }
  };

  const goalProgress = wallet?.goalAmount
    ? Math.min((wallet.totalContributions / wallet.goalAmount) * 100, 100)
    : 0;

  const animatedBalance = useAnimatedCounter((wallet?.balance || 0) / 100, { duration: 900 });
  const animatedContributions = useAnimatedCounter((wallet?.totalContributions || 0) / 100, { duration: 900, delay: 100 });
  const animatedSpent = useAnimatedCounter((wallet?.totalSpent || 0) / 100, { duration: 900, delay: 200 });
  const { fire: fireConfetti, ConfettiCanvas } = useConfetti();

  const prevBalanceRef = useRef(wallet?.balance || 0);
  const balanceCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const currentBalance = wallet?.balance || 0;
    if (prevBalanceRef.current !== currentBalance && currentBalance > prevBalanceRef.current && prevBalanceRef.current > 0) {
      balanceCardRef.current?.classList.remove("balance-pop");
      void balanceCardRef.current?.offsetWidth;
      balanceCardRef.current?.classList.add("balance-pop");
    }
    prevBalanceRef.current = currentBalance;
  }, [wallet?.balance]);

  const prevGoalRef = useRef(goalProgress);
  const progressBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (wallet?.goalAmount && wallet.goalAmount > 0) {
      const milestones = [25, 50, 75, 100];
      const prevProgress = prevGoalRef.current;
      milestones.forEach(m => {
        if (goalProgress >= m && prevProgress < m && prevProgress > 0) {
          fireConfetti(progressBarRef.current || undefined);
          if (m === 100) {
            progressBarRef.current?.classList.add("animate-milestone-shine");
            setTimeout(() => progressBarRef.current?.classList.remove("animate-milestone-shine"), 1500);
          }
        }
      });
    }
    prevGoalRef.current = goalProgress;
  }, [goalProgress, wallet?.goalAmount, fireConfetti]);

  if (walletLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="max-w-4xl mx-auto space-y-5 sm:space-y-8">
        <div className="text-center space-y-4 py-6 sm:py-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
            <Heart className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">HealthWallet</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Pre-fund your healthcare expenses with community support through micro-contributions from your support network.
            Family, friends, and community members can contribute directly to your healthcare costs.
          </p>
        </div>

        <Card className="max-w-lg mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Create Your Health Wallet
            </CardTitle>
            <CardDescription>
              Set up your wallet to start receiving contributions for your healthcare needs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/wallet/create">
              <Button className="w-full" size="lg" data-testid="button-create-wallet">
                <Plus className="h-4 w-4 mr-2" />
                Get Started
              </Button>
            </Link>
          </CardContent>
        </Card>

        {communityFunds && communityFunds.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-center">Community Support Funds</h2>
            <p className="text-muted-foreground text-center max-w-lg mx-auto">
              These community-managed funds provide additional support for patients in need.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {communityFunds.map((fund) => (
                <Card key={fund.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{fund.name}</CardTitle>
                    <Badge variant="secondary">{fund.category}</Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{fund.description}</p>
                    <div className="mt-4 flex justify-between gap-2 text-sm">
                      <span>Raised: {formatCents(fund.totalRaised)}</span>
                      <span>Distributed: {formatCents(fund.totalDistributed)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const getWalletTypeIcon = (type?: string) => {
    switch (type) {
      case "family":
        return <Users className="h-6 w-6 text-primary" />;
      case "community":
        return <Heart className="h-6 w-6 text-primary" />;
      case "organization":
        return <Building2 className="h-6 w-6 text-primary" />;
      default:
        return <Wallet className="h-6 w-6 text-primary" />;
    }
  };

  const getWalletTypeLabel = (type?: string) => {
    switch (type) {
      case "individual":
        return "Individual Wallet";
      case "family":
        return "Family Wallet";
      case "community":
        return "Community Wallet";
      case "organization":
        return "Organization Wallet";
      default:
        return "Health Wallet";
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      <ConfettiCanvas />
      <div className="flex items-center justify-between gap-2 mb-4">
        <Link href="/wallet" className="text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="link-back-to-hub">
          <ArrowLeft className="h-4 w-4" />
          Back to Hub
        </Link>
        {wallet.walletType === "individual" && (
          <Link href="/wallet/create">
            <Button variant="outline" size="sm" data-testid="button-create-new-wallet">
              <Plus className="h-4 w-4 mr-2" />
              Create Family/Community Wallet
            </Button>
          </Link>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
            {getWalletTypeIcon(wallet.walletType)}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-wallet-title">
                {wallet.organizationName || wallet.patientName}'s {getWalletTypeLabel(wallet.walletType)}
              </h1>
              {wallet.walletType && wallet.walletType !== "individual" && (
                <Badge variant="outline" className="capitalize" data-testid="badge-wallet-type">
                  {wallet.walletType}
                </Badge>
              )}
              {wallet.verificationStatus === "verified" && (
                <Badge variant="default" data-testid="badge-verified">
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              HealthWallet
              {wallet.organizationType && (
                <span> • {wallet.organizationType.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="button-copy-share-link">
            <Copy className="h-4 w-4 mr-2" />
            Copy Share Link
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="button-preview-share-page">
            <a href={`/wallet/contribute/${wallet.shareToken}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview
            </a>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Available Funds</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div ref={balanceCardRef} className="text-2xl sm:text-3xl font-bold text-primary" data-testid="text-wallet-balance">
              ${animatedBalance.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">Ready for healthcare expenses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Contributions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-green-600" data-testid="text-total-contributions">
              ${animatedContributions.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">From your support network</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold" data-testid="text-total-spent">
              ${animatedSpent.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">Applied to healthcare expenses</p>
          </CardContent>
        </Card>
      </div>

      {wallet.goalAmount && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
              <span>Funding Progress</span>
              <span className="text-muted-foreground">
                {formatCents(wallet.totalContributions)} of {formatCents(wallet.goalAmount)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div ref={progressBarRef}>
              <Progress value={goalProgress} className="h-3" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">{goalProgress.toFixed(1)}% of goal reached</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          {(wallet?.walletType === "family" || wallet?.walletType === "community" || wallet?.walletType === "organization") && (
            <TabsTrigger value="beneficiaries" data-testid="tab-beneficiaries">
              <Users className="h-4 w-4 mr-1" />
              Beneficiaries
            </TabsTrigger>
          )}
          <TabsTrigger value="payments" data-testid="tab-payments">
            <Hospital className="h-4 w-4 mr-1" />
            Provider Payments
          </TabsTrigger>
          <TabsTrigger value="share" data-testid="tab-share">Share</TabsTrigger>
          {(wallet?.walletType === "family" || wallet?.walletType === "community" || wallet?.walletType === "organization") && (
            <TabsTrigger value="team" data-testid="tab-team">
              <UserPlus className="h-4 w-4 mr-1" />
              Team
            </TabsTrigger>
          )}
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4">
          {isOwner && <ThankYouPrompt walletId={wallet.id} />}
          {isOwner && <SentThankYous />}
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>
                Contributions received and payments made from your health wallet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : transactions && transactions.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between gap-2 p-4 rounded-lg border"
                        data-testid={`transaction-row-${tx.id}`}
                      >
                        <div className="flex items-center gap-4">
                          {tx.amount > 0 ? (
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20">
                              <ArrowDownCircle className="h-5 w-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20">
                              <ArrowUpCircle className="h-5 w-5 text-blue-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">
                              {tx.type === "contribution"
                                ? tx.contributorName || "Anonymous Contribution"
                                : tx.type === "order_payment"
                                ? "Healthcare Payment"
                                : tx.type === "subscription"
                                ? "Monthly Support"
                                : tx.description || "Transaction"}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {tx.createdAt ? format(new Date(tx.createdAt), "MMM d, yyyy 'at' h:mm a") : "N/A"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-semibold ${
                              tx.amount > 0 ? "text-green-600" : "text-foreground"
                            }`}
                          >
                            {tx.amount > 0 ? "+" : ""}
                            {formatCents(Math.abs(tx.amount))}
                          </p>
                          <Badge variant={tx.status === "completed" ? "default" : "secondary"} className="text-xs">
                            {tx.status === "completed" ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : null}
                            {tx.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No transactions yet</p>
                  <p className="text-sm">Share your wallet link to start receiving contributions.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Beneficiaries Tab - for family/community/org wallets */}
        {(wallet.walletType === "family" || wallet.walletType === "community" || wallet.walletType === "organization") && (
          <TabsContent value="beneficiaries" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Wallet Beneficiaries
                  </CardTitle>
                  <CardDescription>
                    Manage people who can receive healthcare services funded by this wallet.
                  </CardDescription>
                </div>
                <Dialog open={isAddBeneficiaryOpen} onOpenChange={setIsAddBeneficiaryOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="button-add-beneficiary">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Beneficiary
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Beneficiary</DialogTitle>
                      <DialogDescription>
                        Add a person who can receive healthcare services funded by this wallet.
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target as HTMLFormElement);
                        addBeneficiaryMutation.mutate({
                          firstName: formData.get("firstName") as string,
                          lastName: formData.get("lastName") as string,
                          relationship: formData.get("relationship") as string,
                          email: formData.get("email") as string,
                          phone: formData.get("phone") as string,
                          spendingLimit: formData.get("spendingLimit") ? parseFloat(formData.get("spendingLimit") as string) : undefined,
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">First Name</label>
                          <Input name="firstName" required data-testid="input-beneficiary-first-name" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Last Name</label>
                          <Input name="lastName" required data-testid="input-beneficiary-last-name" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Relationship</label>
                        <Select name="relationship">
                          <SelectTrigger data-testid="select-relationship">
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spouse">Spouse</SelectItem>
                            <SelectItem value="child">Child</SelectItem>
                            <SelectItem value="parent">Parent</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Email (Optional)</label>
                        <Input name="email" type="email" data-testid="input-beneficiary-email" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Phone (Optional)</label>
                        <Input name="phone" data-testid="input-beneficiary-phone" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Spending Limit (Optional)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input name="spendingLimit" type="number" step="0.01" className="pl-10" data-testid="input-beneficiary-spending-limit" />
                        </div>
                        <p className="text-xs text-muted-foreground">Maximum amount this beneficiary can use from the wallet</p>
                      </div>
                      <Button type="submit" className="w-full" disabled={addBeneficiaryMutation.isPending} data-testid="button-submit-beneficiary">
                        {addBeneficiaryMutation.isPending ? "Adding..." : "Add Beneficiary"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {beneficiariesLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : beneficiaries && beneficiaries.length > 0 ? (
                  <div className="space-y-4">
                    {beneficiaries.map((ben) => (
                      <div
                        key={ben.id}
                        className="flex items-center justify-between gap-2 p-4 rounded-lg border"
                        data-testid={`beneficiary-row-${ben.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{ben.firstName} {ben.lastName}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {ben.relationship && <span className="capitalize">{ben.relationship}</span>}
                              {ben.email && <span>• {ben.email}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {ben.spendingLimit && (
                              <p className="text-sm font-medium">Limit: {formatCents(ben.spendingLimit)}</p>
                            )}
                            <p className="text-sm text-muted-foreground">Used: {formatCents(ben.totalSpent || 0)}</p>
                          </div>
                          <Badge variant={ben.verificationStatus === "verified" ? "default" : "secondary"}>
                            {ben.verificationStatus === "verified" ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <Clock className="h-3 w-3 mr-1" />
                            )}
                            {ben.verificationStatus}
                          </Badge>
                          <Badge variant={ben.isActive ? "default" : "outline"}>
                            {ben.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No beneficiaries added yet</p>
                    <p className="text-sm">Add family members or others who can use this wallet for healthcare.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Provider Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hospital className="h-5 w-5" />
                Provider Payments
              </CardTitle>
              <CardDescription>
                Direct payments to healthcare providers for services rendered.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : providerPayments && providerPayments.length > 0 ? (
                <div className="space-y-4">
                  {providerPayments.map((payment) => {
                    const provider = healthcareProviders?.find(p => p.id === payment.providerId);
                    return (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between gap-2 p-4 rounded-lg border"
                        data-testid={`payment-row-${payment.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                            payment.status === "completed" ? "bg-green-100 dark:bg-green-900/20" :
                            payment.status === "pending" ? "bg-yellow-100 dark:bg-yellow-900/20" :
                            payment.status === "rejected" ? "bg-red-100 dark:bg-red-900/20" :
                            "bg-blue-100 dark:bg-blue-900/20"
                          }`}>
                            {payment.status === "completed" ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : payment.status === "pending" ? (
                              <Clock className="h-5 w-5 text-yellow-600" />
                            ) : payment.status === "rejected" ? (
                              <XCircle className="h-5 w-5 text-red-600" />
                            ) : (
                              <Send className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{provider?.name || "Healthcare Provider"}</p>
                            <p className="text-sm text-muted-foreground">
                              {payment.serviceDescription || "Healthcare services"}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {format(new Date(payment.requestedAt), "MMM d, yyyy")}
                              {payment.invoiceNumber && (
                                <span>• Invoice: {payment.invoiceNumber}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-lg">
                            {formatCents(payment.amount || 0)}
                          </p>
                          <Badge 
                            variant={
                              payment.status === "completed" ? "default" :
                              payment.status === "pending" ? "secondary" :
                              payment.status === "rejected" ? "destructive" :
                              "outline"
                            }
                          >
                            {payment.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Hospital className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No provider payments yet</p>
                  <p className="text-sm">Payments to healthcare providers will appear here.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="share" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Share Your Wallet
              </CardTitle>
              <CardDescription>
                Invite family, friends, and community members to contribute to your healthcare costs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
              <div className="p-3 sm:p-4 rounded-lg bg-muted">
                <p className="text-sm font-medium mb-2">Your Share Link</p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/wallet/contribute/${wallet.shareToken}`}
                    className="font-mono text-sm"
                    data-testid="input-share-link"
                  />
                  <Button size="icon" variant="outline" onClick={copyShareLink} data-testid="button-copy-link-icon">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">How It Works</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 rounded-lg border text-center">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mx-auto mb-3">
                      <Share2 className="h-5 w-5 text-primary" />
                    </div>
                    <h5 className="font-medium mb-1">1. Share Link</h5>
                    <p className="text-sm text-muted-foreground">
                      Send your wallet link to family and friends
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 rounded-lg border text-center">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mx-auto mb-3">
                      <Heart className="h-5 w-5 text-primary" />
                    </div>
                    <h5 className="font-medium mb-1">2. Receive Support</h5>
                    <p className="text-sm text-muted-foreground">
                      Contributors can make one-time or recurring contributions
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 rounded-lg border text-center">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mx-auto mb-3">
                      <Wallet className="h-5 w-5 text-primary" />
                    </div>
                    <h5 className="font-medium mb-1">3. Apply to Care</h5>
                    <p className="text-sm text-muted-foreground">
                      Funds are automatically applied to your healthcare expenses
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {(wallet?.walletType === "family" || wallet?.walletType === "community" || wallet?.walletType === "organization") && (
          <TabsContent value="team" className="space-y-4">
            <WalletCollaboration
              walletId={wallet.id}
              wallet={wallet as any}
              isOwner={isOwner || false}
            />
          </TabsContent>
        )}

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Wallet Settings</CardTitle>
              <CardDescription>Manage your wallet preferences and visibility.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <p className="font-medium">Public Visibility</p>
                  <p className="text-sm text-muted-foreground">
                    {wallet.isPublic
                      ? "Your wallet is visible in community browsing and public discovery"
                      : "Your wallet is private - only people with your direct link can contribute"}
                  </p>
                </div>
                <Switch
                  checked={wallet.isPublic ?? false}
                  onCheckedChange={handleTogglePublic}
                  disabled={isTogglingPublic}
                  data-testid="switch-wallet-public"
                />
              </div>

              <Separator />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <p className="font-medium">Category</p>
                  <p className="text-sm text-muted-foreground">
                    Select the healthcare category that best describes your needs
                  </p>
                </div>
                <Select 
                  value={wallet.category ?? undefined} 
                  onValueChange={handleCategoryChange}
                  disabled={updateWalletSettingsMutation.isPending}
                >
                  <SelectTrigger className="w-full sm:w-[200px]" data-testid="select-wallet-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="specialty_medication">Specialty Treatment</SelectItem>
                    <SelectItem value="chronic_condition">Chronic Condition</SelectItem>
                    <SelectItem value="cancer_treatment">Cancer Treatment</SelectItem>
                    <SelectItem value="mental_health">Mental Health</SelectItem>
                    <SelectItem value="elder_care">Elder Care</SelectItem>
                    <SelectItem value="pediatric">Pediatric Care</SelectItem>
                    <SelectItem value="surgical">Surgical Care</SelectItem>
                    <SelectItem value="transplant">Transplant</SelectItem>
                    <SelectItem value="rehabilitation">Rehabilitation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium">Wallet Type</p>
                  <p className="text-sm text-muted-foreground">
                    {wallet.walletType === "individual" && "Personal wallet for individual healthcare needs"}
                    {wallet.walletType === "family" && "Shared wallet for family members' healthcare"}
                    {wallet.walletType === "community" && "Community-based wallet for local groups"}
                    {wallet.walletType === "organization" && "Organizational wallet for institutions"}
                  </p>
                </div>
                <Badge variant="outline" className="capitalize">
                  {wallet.walletType || "individual"}
                </Badge>
              </div>

              <Separator />

              <div>
                <p className="font-medium mb-2">Wallet Created</p>
                <p className="text-sm text-muted-foreground">
                  {wallet.createdAt ? format(new Date(wallet.createdAt), "MMMM d, yyyy") : "N/A"}
                </p>
              </div>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
              {updateWalletSettingsMutation.isPending && (
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 animate-spin" />
                  Saving changes...
                </span>
              )}
              {!updateWalletSettingsMutation.isPending && (
                <span>Changes are saved automatically.</span>
              )}
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
